import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { CapsuleButton, ContentSpace, MainPageWithButtons } from "../_components";
import { Selectors } from "../_reducers/app.reducer";
import { ClockinActions } from "../_stores/ClockinStore/ClockinActions";
import { CustomDropdown } from "../_components/CustomDropdown";
import { User } from "../_models/User";
import { IsoStartEndDatePicker } from "../_components/IsoStartEndDatePicker";
import moment from "moment";
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { LocationModel } from "../_models/LocationModel";
import CustomToolbar from "./ClockinsCalendarToolbar";
import { appPermissions } from "../_constants";
import { CustomModal } from "../_components/CustomModal";
import { InvitationsHelper } from '../_helpers';
import { TimeHelper } from '../_helpers';

const usersArray = [User()];

export function ClockinsVacationModule() {

    //LIBRARIES
    const dispatch = useDispatch();
    const { t } = useTranslation();

    //GLOBAL STATE
    const locationUid = useSelector(Selectors.getSelectedLocationUid);
    const permission        = useSelector(Selectors.getPermissionSelectedLocation);
    const canManageClockins = appPermissions.canManageClockins(permission);
    const myUserUid         = useSelector(Selectors.getUserUid);
    const vacation = useSelector(Selectors.getClockinsVacation);

    let users = usersArray;
    users = useSelector(Selectors.getClockinsUsers);

    let selectedLocation = LocationModel();
    selectedLocation = useSelector(state => Selectors.getLocation(state, locationUid));

    const localizer = momentLocalizer(moment);

    //LOCAL STATE
    const [userUid, setUserUid] = useState(myUserUid);
    const [begin, setBegin] = useState(null);
    const [end, setEnd] = useState(null);
    const [events, setEvents] = useState([]);
    const [absenceType, setAbsenceType] = useState(0);

    const [showModal, setShowModal] = useState(false);
    const [eventBegin, setEventBegin] = useState(null);
    const [eventEnd, setEventEnd] = useState(null);
    const [selectedEvent, setSelectedEvent] = useState(null);

    const [errorMessage, setErrorMessage] = useState('');

    const showErrorMessage = (message) => {
        setErrorMessage(message);
        setTimeout(() => setErrorMessage(''), 4000);
    }

    function setBeginEndCalendar(dt) {
        let beginDate = TimeHelper.getFirstDayOfCalendarMonth(dt);
        let endDate = TimeHelper.getLastDayOfCalendarMonth(dt);

        setBegin(beginDate.format('YYYY-MM-DD'));
        setEnd(endDate.format('YYYY-MM-DD'));
    }

    //INITIALIZATION
    useEffect(() => {
        setBeginEndCalendar(new Date());

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, locationUid]); // No se puede poner "users" para arreglar warning porque crea un bucle 
    
    useEffect(() => {
        const date = TimeHelper.getTodayIsoString();
        const beginUsers = TimeHelper.getFirstDayOfWeekForIso(date)
        const endUsers = TimeHelper.getLastDayOfWeekForIso(date);
        dispatch(ClockinActions.listClockinUsers(locationUid, beginUsers, endUsers, canManageClockins));
    }, [dispatch, canManageClockins, locationUid]);


    useEffect(() => {
        dispatch(ClockinActions.selectUserUid(userUid));
        dispatch(ClockinActions.listVacation(locationUid, begin, end, userUid));
    }, [dispatch, userUid]);

    useEffect(() => {
        if (showModal)
            return;
        dispatch(ClockinActions.listVacation(locationUid, begin, end, userUid));
     }, [dispatch, begin, end]);

     useEffect(() => {
        let eventList = [];
        vacation.forEach((v) => {
            let title;
            switch (v.type) {
                case 0:
                    title = t("mod_clockins_absence_type_vacation");
                    break
                case 1:
                    title = t("mod_clockins_absence_type_sick_leave");
                    break;
                case 2:
                    title = t("mod_clockins_absence_type_public_holiday");
                    break;
            }

            eventList.push({
                id: v.uid,
                title,
                start: v.begin,
                end: v.end,
                type: v.type
            });
        });
        setEvents(eventList);
     }, [dispatch, vacation]);

    //ACTION HANDLERS
    function createEvent(slotInfo) {
        if (!canManageClockins)
            return;

        slotInfo.id = moment().format('x');  //Generate id with Unix Millisecond Timestamp
        slotInfo.title = null;
        slotInfo.location = null;
        
        const dateBegin = moment(slotInfo.start).format('YYYY-MM-DD');
        const dateEnd = moment(slotInfo.end).subtract(1, 'day').format('YYYY-MM-DD');
        setEventBegin(dateBegin);
        setEventEnd(dateEnd);
        setShowModal(true);
    }

    function showEvent(slotInfo) {
        setSelectedEvent(slotInfo.id);
        setEventBegin(slotInfo.start);
        setEventEnd(slotInfo.end);
        setShowModal(true);
    }

    const requestVacation = () => {
        if (eventBegin > eventEnd) {
            showErrorMessage(t("error_600_begin_greater_than_end"));
            return;
        } else if (selectedEvent != null) {
            setEventBegin(null);
            setEventEnd(null);
            setShowModal(false);
        } else {
            dispatch(request);
        }

        async function request() {
            await dispatch(ClockinActions.requestVacation(eventBegin, eventEnd, absenceType));
            dispatch(ClockinActions.listVacation(locationUid, begin, end, userUid));
            setEventBegin(null);
            setEventEnd(null);
            setShowModal(false);
        }
    };

    const deleteVacation = () => {
        
        dispatch(asyncAction);

        async function asyncAction() {
            await dispatch(ClockinActions.deleteVacation(selectedEvent));

            dispatch(ClockinActions.listVacation(locationUid, begin, end, userUid));

            setEventBegin(null);
            setEventEnd(null);
            setSelectedEvent(null);
            setShowModal(false);
        }
    };

    const handleCalendarNavigate = (date) => {
        setBeginEndCalendar(date);
    };

    //VIEW
    return (
        <ContentSpace
            mainContent={
                <MainPageWithButtons
                    pageTitle={t("global_absences")}
                    content={content()}
                    size={'LARGE'}
                />
            }
        />
    );

    function content() {
        return (
        <>
            { canManageClockins && <div className="
                    flex-shrink-0
                    d-flex
                    justify-content-between
                ">
                    <CustomDropdown
                        title={t("global_user")}
                        value={userUid}
                        onChange={setUserUid}
                        sortAlphabetically
                        options={optionsFn()}
                        isReadOnly={false}
                    />
                    
                    <div className="ml-2"/>
                </div>
            }
            <div className="mt-2" />
            <div className="calendar-container">
                <Calendar
                    popup
                    selectable
                    localizer={localizer}
                    views={['month']}
                    style={{height: 600}}
                    components={{toolbar: CustomToolbar}}
                    eventPropGetter={event => {
                        let background = '#DE6987';
                        switch (event.type) {
                            case 0:
                                background = '#8CBD4C';
                                break;
                            case 1:
                                background = '#30c3d1';
                                break;
                            case 2:
                                background = '#DE6987';
                                break;
                        }
                        return {
                            style: {
                                backgroundColor: background
                            }
                        };
                    }}
                    onSelectEvent={(slotInfo) => showEvent(slotInfo)}
                    onSelectSlot={(slotInfo) => createEvent(slotInfo)}
                    onNavigate={handleCalendarNavigate}
                    events={events}
                    />
                <CustomModal
                    show={showModal}
                    centered
                    title={(selectedEvent != null) ? t("global_edit"):t("mod_clockins_add_absence")}
                    onHide={() => setShowModal(false)}
                    content={
                        <div>
                            <CustomDropdown
                                title={t("mod_clockins_absence_type_title")}
                                value={absenceType}
                                onChange={setAbsenceType}
                                options={absenceTypes()}
                                isReadOnly={false}
                            />
                            
                            <div className="mb-2"/>
                            <IsoStartEndDatePicker
                                handleStartDateChange={(selectedBegin) => {
                                    let dateBegin = new Date(selectedBegin);
                                    setEventBegin(moment(dateBegin).format('YYYY-MM-DD'));
                                }}
                                handleEndDateChange={(selectedEnd) => {
                                    let dateEnd = new Date(selectedEnd);
                                    setEventEnd(moment(dateEnd).format('YYYY-MM-DD'));
                                }}
                                startIsoDate={eventBegin}
                                endIsoDate={eventEnd}
                                dateOnly={true}
                                isEnabled={selectedEvent == null}
                            />
                            {errorMessage ? (
                                <div className="text-danger pl-1 pt-1">
                                    {errorMessage}
                                </div>
                            ) : (
                                <div></div>
                            )}
                            <div className="mb-2"/>
                        </div>
                    }
                    footer={
                        <>
                            {canManageClockins && selectedEvent != null && 
                                <CapsuleButton
                                    style="DANGER"
                                    text={t("global_delete")}
                                    onClick={() => {
                                        deleteVacation();
                                    }}
                                    />
                            }
                            <CapsuleButton
                                style="DANGER"
                                text={t("global_cancel")}
                                onClick={() => {
                                    setEventBegin(null);
                                    setEventEnd(null);
                                    setSelectedEvent(null);
                                    setShowModal(false);
                                }}
                                />
                            {canManageClockins && selectedEvent == null &&
                                <CapsuleButton
                                    isEnabled={true}
                                    text={t("global_save")}
                                    onClick={() => {
                                        requestVacation();
                                    }}
                                />
                            }
                        </>
                    }
                />
            </div>
        </>);

        function optionsFn() {
            return users.map(user =>
                CustomDropdown.buildOption(user.user.uid, InvitationsHelper.getUiUserNameFromUser(user.user))
            );
        }

        function absenceTypes() {
            return [
                CustomDropdown.buildOption(0, t("mod_clockins_absence_type_vacation")),
                CustomDropdown.buildOption(1, t("mod_clockins_absence_type_sick_leave")),
                CustomDropdown.buildOption(2, t("mod_clockins_absence_type_public_holiday")),
            ];
        }
    }
}